import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@lightning/utils';

// Module-level cache of images that have been loaded in this session
// This persists across component unmounts/remounts
const loadedImages = new Set<string>();

// Default gray placeholder as a data URI - square aspect ratio for book covers
const DEFAULT_PLACEHOLDER = 'data:image/svg+xml;base64,' + btoa(`
  <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
    <rect width="400" height="400" fill="#e5e7eb"/>
  </svg>
`);

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  placeholder?: string;
  alt: string;
  className?: string;
  onLoad?: () => void;
  onError?: (error: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  rootMargin?: string;
  threshold?: number;
}

export const LazyImage: React.FC<LazyImageProps> = ({
  src,
  placeholder,
  alt,
  className,
  onLoad,
  onError,
  rootMargin = '100px',
  threshold = 0.01,
  ...props
}) => {
  // Check if this image was already loaded in this session
  const wasAlreadyLoaded = loadedImages.has(src);

  // If already loaded, skip lazy loading entirely
  const [imageSrc, setImageSrc] = useState<string>(
    wasAlreadyLoaded ? src : (placeholder || DEFAULT_PLACEHOLDER)
  );
  const [isLoading, setIsLoading] = useState(!wasAlreadyLoaded);
  const [hasError, setHasError] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(wasAlreadyLoaded);

  useEffect(() => {
    // Skip observer if image was already loaded
    if (wasAlreadyLoaded) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.disconnect();
        }
      },
      {
        rootMargin,
        threshold
      }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, threshold, wasAlreadyLoaded]);

  useEffect(() => {
    if (!isIntersecting || wasAlreadyLoaded) return;

    const img = new Image();

    img.onload = () => {
      loadedImages.add(src); // Mark as loaded for future navigations
      setImageSrc(src);
      setIsLoading(false);
      onLoad?.();
    };

    img.onerror = () => {
      setHasError(true);
      setIsLoading(false);
      // Keep the placeholder on error
      setImageSrc(placeholder || DEFAULT_PLACEHOLDER);
    };

    img.src = src;
  }, [src, isIntersecting, onLoad, placeholder, wasAlreadyLoaded]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setHasError(true);
    setImageSrc(placeholder || DEFAULT_PLACEHOLDER);
    onError?.(e);
  };

  return (
    <>
      <img
        ref={imageRef}
        src={imageSrc}
        alt={alt}
        className={cn(
          'transition-opacity duration-300',
          className
        )}
        onError={handleError}
        style={{
          backgroundColor: isLoading ? 'rgb(229 231 235)' : undefined,
          ...props.style
        }}
        {...props}
      />
    </>
  );
};

// Export a simple wrapper for images that don't need lazy loading
export const OptimizedImage: React.FC<React.ImgHTMLAttributes<HTMLImageElement>> = ({
  loading = 'lazy',
  decoding = 'async',
  ...props
}) => {
  return <img loading={loading} decoding={decoding} {...props} />;
};
